/* Catching the price that was meant as a total.
 *
 * "Average cost" is per share, and the field next to it is a share count, and
 * people type what they paid — which for three shares of a fifteen-dollar stock
 * is forty-three dollars. The book then does correct arithmetic on it and
 * charges the account three times what left it, sets an average nearly three
 * times the market, and reports a loss that never happened. Every figure
 * downstream is right; the one at the top was wrong.
 *
 * The app is holding the thing that gives it away: it knows the market price
 * while it is being told a different one. So it says so — a price well away
 * from the mark is stated as what it is, along with what the number would mean
 * read as a total, and whether that reading lands where the market is.
 *
 * It never refuses and never corrects. A price genuinely far from today's mark
 * is ordinary — a position bought two years ago, a stock that has halved — and
 * a book that argued with those would be wrong more often than it was right.
 * This puts the arithmetic on screen and lets the person who was there decide. */

export interface PriceDoubt {
  /** How many times the current mark the entered price is. */
  multiple: number;
  /** What one share would have cost if the figure typed was the whole bill. */
  ifTotal: number;
  /** True when that reading lands near the mark, which is the actual tell. */
  totalLooksRight: boolean;
}

/** How far from the mark a price has to be before it is worth mentioning.
    Wide, because a real cost basis is often a long way from today's price. */
const FAR = 1.6;
/** How close the total-reading has to land to be worth suggesting. */
const NEAR = 0.25;

export function doubtAboutPrice(
  entered: number, mark: number, shares: number,
): PriceDoubt | null {
  if (!(entered > 0) || !(mark > 0) || !(shares > 1)) return null;

  const multiple = entered / mark;
  if (multiple <= FAR && multiple >= 1 / FAR) return null;

  const ifTotal = entered / shares;
  return {
    multiple,
    ifTotal,
    totalLooksRight: Math.abs(ifTotal - mark) / mark <= NEAR,
  };
}
